import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Plus,
  X,
  Edit3,
  Calendar,
  Tag,
  Sparkles,
  Settings2,
  Info,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ALL_CLASSES } from '@/lib/ontology/schema'
import { useConnectionStore } from '@/stores/connectionStore'
import { getLocalName } from '@/lib/ontology/namespaces'
import { ENTITY_TYPE_NAMESPACES, resolveEntityType } from '@/lib/interviewer/entityCreation'
import { InstanceDetailPanel } from '@/components/shared/InstanceDetailPanel'
import { CreateInstanceDialog } from '@/components/shared/CreateInstanceDialog'
import { CanvasInstanceNavigator } from './CanvasInstanceNavigator'
import { CreateCanvasInstanceDialog } from './CreateCanvasInstanceDialog'
import { PhotoSectionCard } from './PhotoSectionCard'
import { SectionSuggestionsPopup } from './SectionSuggestionsPopup'
import { SectionSuggestionsPanel } from './SectionSuggestionsPanel'
import { PersonaChipList } from './PersonaChipList'
import { CanvasAttribution } from './CanvasAttribution'
import { usePersonasForCanvas, usePersonaWorkshopSessionsMap } from '@/hooks/usePersonasForCanvas'
import { CanvasCoachPanel, type EntityControlFunctions } from './CanvasCoachPanel'
import { CanvasBMCOutlineView } from './CanvasBMCOutlineView'
import { useAIStore } from '@/stores/aiStore'
import { UnifiedDocumentPanel } from '@/components/document-processing'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import type { Document2WithEntities, Document2Entity } from '@/lib/api/client'
import { canvasAIApi, sparqlApi } from '@/lib/api/client'
import {
  CANVAS_TEMPLATES,
  BUSINESS_MODEL_CANVAS,
  getTemplatesForDomain,
  loadDomainConfig,
  saveDomainConfig,
  type CanvasTemplate,
  type CanvasSection,
  type CanvasDomain,
  type CanvasDomainConfig,
} from './canvasTemplates'
import { alpha, getCanvasSectionThemeStyles } from './canvasTheme'
import { CanvasDomainConfigModal } from './CanvasDomainConfigModal'
import {
  useCanvasInstances,
  useCreateCanvasInstance,
  useUpdateCanvasInstance,
  useCanvasInstance,
  useCanvasSectionData,
  useUpdateSectionNotes,
  useEnsureDefaultCanvas,
  useLinkItemToSection,
  useUnlinkItemFromSection,
  useSyncCanvasEntities,
} from '@/hooks/useCanvasInstances'
import type { ExtractedEntity } from '@/hooks/useCanvasCoach'
import { cn } from '@/lib/utils'

// Map template IDs to canvas types for Canvas Coach
// These must match the CanvasType enum in the backend and ai_workflows.canvas_type in the database
const TEMPLATE_TO_CANVAS_TYPE: Record<string, string> = {
  'business-model-canvas': 'bmc',
  'value-proposition-canvas': 'vpc',
  'positioning-canvas': 'positioning',
  'problem-statement-canvas': 'psc',
  'persona-canvas': 'persona',
}

// Default instance names per template
const DEFAULT_INSTANCE_NAMES: Record<string, string> = {
  'business-model-canvas': 'Main Business Model',
  'value-proposition-canvas': 'Main Value Proposition',
  'positioning-canvas': 'Main Positioning',
  'problem-statement-canvas': 'Main Problem Statement',
  'persona-canvas': 'Primary Persona',
}

function getAllowedSectionTypeNames(classUris: string[]): string[] {
  return classUris
    .map((uri) => {
      const hashIndex = uri.lastIndexOf('#')
      return hashIndex >= 0 ? uri.slice(hashIndex + 1) : uri
    })
    .filter(Boolean)
}

function filterLinkedItemsForSection(
  linkedItems: Array<{ instanceUri: string; label: string; note?: string; classUri?: string }> | undefined,
  classUris: string[],
) {
  if (!linkedItems?.length) return []

  const allowedTypeNames = getAllowedSectionTypeNames(classUris)
  if (allowedTypeNames.length === 0) return linkedItems

  return linkedItems.filter((item) => {
    if (!item.classUri) return true

    const itemType = item.classUri.includes('#')
      ? item.classUri.slice(item.classUri.lastIndexOf('#') + 1)
      : item.classUri

    return allowedTypeNames.includes(itemType)
  })
}

function getDisallowedLinkedItemsForSection(
  linkedItems: Array<{ instanceUri: string; label: string; note?: string; classUri?: string }> | undefined,
  classUris: string[],
) {
  if (!linkedItems?.length) return []

  const allowedTypeNames = getAllowedSectionTypeNames(classUris)
  if (allowedTypeNames.length === 0) return []

  return linkedItems.filter((item) => {
    if (!item.classUri) return false

    const itemType = item.classUri.includes('#')
      ? item.classUri.slice(item.classUri.lastIndexOf('#') + 1)
      : item.classUri

    return !allowedTypeNames.includes(itemType)
  })
}

interface CanvasExplorerProps {
  /** Hide AI Coach and Document panels (e.g., when embedded in workspace) */
  hideAuxiliaryPanels?: boolean
  /** Hide template tabs, description, and instance navigator (managed externally by InteractiveCanvasContainer) */
  hideHeader?: boolean
  /** Externally controlled template (from InteractiveCanvasContainer) */
  template?: CanvasTemplate
  /** Externally controlled instance URI (from InteractiveCanvasContainer) */
  instanceUri?: string | null
  /** Filter templates to a specific domain (market/business/solution) */
  domain?: CanvasDomain
}

export function CanvasExplorer({ hideAuxiliaryPanels = false, hideHeader = false, template, instanceUri, domain }: CanvasExplorerProps = {}) {
  const { isConnected } = useConnectionStore()
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const { apiKey } = useAIStore()
  const projectId = currentProject?.id ?? ''
  const [domainConfig, setDomainConfig] = useState<CanvasDomainConfig>(() => loadDomainConfig(projectId))
  const [showConfigModal, setShowConfigModal] = useState(false)

  const visibleTemplates = domain ? getTemplatesForDomain(domain, domainConfig) : CANVAS_TEMPLATES
  const defaultTemplate = visibleTemplates[0] ?? BUSINESS_MODEL_CANVAS
  const [internalTemplate, setInternalTemplate] = useState<CanvasTemplate>(defaultTemplate)
  const selectedTemplate = template ?? internalTemplate
  const setSelectedTemplate = (t: CanvasTemplate) => setInternalTemplate(t)

  function handleSaveConfig(config: CanvasDomainConfig) {
    saveDomainConfig(projectId, config)
    setDomainConfig(config)
    // If current template is no longer in the visible list, switch to first available
    if (domain) {
      const newVisible = getTemplatesForDomain(domain, config)
      if (newVisible.length > 0 && !newVisible.find(t => t.id === selectedTemplate.id)) {
        setInternalTemplate(newVisible[0])
      }
    }
    setShowConfigModal(false)
  }
  const [selectedItem, setSelectedItem] = useState<{ uri: string; sectionId: string } | null>(null)
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const [createForSection, setCreateForSection] = useState<CanvasSection | null>(null)

  // Canvas instance state — use external prop when provided (interactive mode)
  const [internalInstanceUri, setInternalInstanceUri] = useState<string | null>(null)
  const selectedInstanceUri = instanceUri !== undefined ? instanceUri : internalInstanceUri
  const setSelectedInstanceUri = (uri: string | null) => setInternalInstanceUri(uri)
  const [, setSelectedInstanceName] = useState<string | null>(null)
  const [showCreateInstanceDialog, setShowCreateInstanceDialog] = useState(false)

  // Property editing state
  const [showPropertyPane, setShowPropertyPane] = useState(false)

  // AI Panel state
  const [showAIPanel, setShowAIPanel] = useState(false)
  const [focusedSectionId, setFocusedSectionId] = useState<string | null>(null)

  // Document Panel state
  const [showDocumentPanel, setShowDocumentPanel] = useState(false)

  // Suggestions Panel state
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false)
  const [suggestionsForSection, setSuggestionsForSection] = useState<CanvasSection | null>(null)

  // Outline view toggle — only available for BMC
  const [viewMode, setViewMode] = useState<'canvas' | 'outline'>('canvas')
  const isBMC = selectedTemplate.id === 'business-model-canvas'

  // Pending entities from AI coach (shown directly on canvas sections)
  const [pendingEntities, setPendingEntities] = useState<ExtractedEntity[]>([])
  const [entityControls, setEntityControls] = useState<EntityControlFunctions | null>(null)

  // Get instances for selected template
  const queryClient = useQueryClient()
  const { data: instances = [], isLoading: instancesLoading } = useCanvasInstances(selectedTemplate.id)
  const createCanvasInstance = useCreateCanvasInstance()
  const updateSectionNotes = useUpdateSectionNotes()
  const linkItemToSection = useLinkItemToSection()
  const unlinkItemFromSection = useUnlinkItemFromSection()
  const ensureDefaultCanvas = useEnsureDefaultCanvas()
  const syncCanvasEntities = useSyncCanvasEntities()

  // Get full instance details for property pane
  const { data: instanceDetails } = useCanvasInstance(selectedInstanceUri)

  // Get section data for AI context
  const { data: sectionData = [] } = useCanvasSectionData(selectedInstanceUri)
  const filteredSectionData = useMemo(
    () =>
      sectionData.map((section) => {
        const templateSection = selectedTemplate.sections.find((candidate) => candidate.id === section.sectionId)
        if (!templateSection) return section

        return {
          ...section,
          linkedItems: filterLinkedItemsForSection(section.linkedItems, templateSection.classUris),
        }
      }),
    [sectionData, selectedTemplate.sections]
  )

  const staleSectionLinks = useMemo(
    () =>
      sectionData.flatMap((section) => {
        const templateSection = selectedTemplate.sections.find((candidate) => candidate.id === section.sectionId)
        if (!templateSection) return []

        return getDisallowedLinkedItemsForSection(section.linkedItems, templateSection.classUris).map((item) => ({
          sectionId: section.sectionId,
          itemUri: item.instanceUri,
          label: item.label,
        }))
      }),
    [sectionData, selectedTemplate.sections]
  )

  // Build canvas context from section data for AI assistant
  const canvasContext = useMemo(() => {
    const context: Record<string, string[]> = {}
    for (const section of filteredSectionData) {
      const items: string[] = []
      // Add notes if present
      if (section.notes?.trim()) {
        items.push(section.notes)
      }
      // Add linked item labels
      for (const item of section.linkedItems || []) {
        if (item.label) {
          items.push(item.label)
        }
        if (item.note) {
          items.push(item.note)
        }
      }
      if (items.length > 0) {
        context[section.sectionId] = items
      }
    }
    return context
  }, [filteredSectionData])

  // Convert canvas context to string for AI duplicate detection
  const canvasContentString = useMemo(() => {
    const parts: string[] = []
    for (const [sectionId, items] of Object.entries(canvasContext)) {
      if (items.length > 0) {
        parts.push(`${sectionId}: ${items.join(', ')}`)
      }
    }
    return parts.join('\n')
  }, [canvasContext])

  // Push canvas data to Skerpa context so the copilot knows what's on screen
  const setPageContext = useSkerpaContextStore(s => s.setPageContext)
  const setActiveResource = useSkerpaContextStore(s => s.setActiveResource)
  useEffect(() => {
    if (instanceUri !== undefined) return undefined
    setActiveResource(selectedInstanceUri)
    return () => setActiveResource(null)
  }, [instanceUri, selectedInstanceUri, setActiveResource])

  useEffect(() => {
    if (!canvasContentString) return
    const templateName = selectedTemplate?.name || 'Canvas'
    setPageContext(`${templateName}\n\n${canvasContentString}`)
  }, [canvasContentString, selectedTemplate?.name, setPageContext])

  // Push canvas section fill state so Skerpa can suggest filling empty sections
  const setCanvasSectionState = useSkerpaContextStore(s => s.setCanvasSectionState)
  useEffect(() => {
    if (!selectedTemplate?.sections) return
    const allSectionIds = selectedTemplate.sections.map(s => s.id)
    const populatedIds = new Set(Object.keys(canvasContext))
    const emptySections = allSectionIds.filter(id => !populatedIds.has(id))
    setCanvasSectionState({
      totalSections: allSectionIds.length,
      populatedSections: allSectionIds.length - emptySections.length,
      emptySections: emptySections.map(id => {
        const section = selectedTemplate.sections.find(s => s.id === id)
        return section?.title || id
      }),
    })
    return () => setCanvasSectionState(null)
  }, [selectedTemplate?.sections, canvasContext, setCanvasSectionState])

  // Remove stale entity links that no longer match the template's allowed classes.
  // We process one at a time and let the invalidated queries refresh before continuing.
  useEffect(() => {
    if (!selectedInstanceUri || unlinkItemFromSection.isPending || staleSectionLinks.length === 0) return

    const staleLink = staleSectionLinks[0]
    unlinkItemFromSection.mutate(
      {
        canvasUri: selectedInstanceUri,
        sectionId: staleLink.sectionId,
        itemUri: staleLink.itemUri,
      },
      {
        onSuccess: () => {
          console.info(
            `Removed stale canvas link "${staleLink.label}" from section "${staleLink.sectionId}"`
          )
        },
        onError: (error) => {
          console.warn(
            `Failed to remove stale canvas link "${staleLink.label}" from section "${staleLink.sectionId}"`,
            error
          )
        },
      }
    )
  }, [selectedInstanceUri, staleSectionLinks, unlinkItemFromSection])

  // Auto-select first instance or create default when template changes
  useEffect(() => {
    if (instancesLoading || !isConnected) return

    if (instances.length > 0) {
      // Select first instance if none selected
      if (!selectedInstanceUri || !instances.find(i => i.uri === selectedInstanceUri)) {
        setSelectedInstanceUri(instances[0].uri)
        setSelectedInstanceName(instances[0].name)
      }
    } else {
      // No instances - auto-create default with auto-populate
      const sections = selectedTemplate.sections.map(section => ({
        sectionId: section.id,
        classUris: section.classUris,
      }))

      const canvasType = TEMPLATE_TO_CANVAS_TYPE[selectedTemplate.id] || 'bmc'

      ensureDefaultCanvas.mutate(
        {
          templateId: selectedTemplate.id,
          templateName: selectedTemplate.name,
          canvasType,
          sections,
        },
        {
          onSuccess: (result) => {
            if (result.created) {
              console.log(`Created default ${selectedTemplate.name} canvas and auto-populated`)
            }
            // Instances will be refetched automatically, which will trigger selection
          },
        }
      )
    }
  }, [instances, instancesLoading, selectedTemplate.id, isConnected])

  // Initialize Canvas AI session when canvas is selected
  useEffect(() => {
    if (!selectedInstanceUri || !currentAccount?.id || !currentProject?.id) return

    // Map template ID to canvas type for the API
    const canvasType = TEMPLATE_TO_CANVAS_TYPE[selectedTemplate.id] || 'bmc'

    // Initialize session (creates if doesn't exist)
    canvasAIApi.getSession(
      currentAccount.id,
      currentProject.id,
      selectedInstanceUri,
      canvasType
    ).catch(error => {
      console.warn('Failed to initialize Canvas AI session:', error)
    })
  }, [selectedInstanceUri, currentAccount?.id, currentProject?.id, selectedTemplate.id])

  // Sync canvas with new entities from graph when canvas loads
  // This picks up entities created elsewhere (e.g., Entity Tree)
  useEffect(() => {
    if (!selectedInstanceUri || !isConnected || syncCanvasEntities.isPending) return

    const sections = selectedTemplate.sections.map(section => ({
      sectionId: section.id,
      classUris: section.classUris,
    }))

    syncCanvasEntities.mutate({
      canvasUri: selectedInstanceUri,
      sections,
    })
  }, [selectedInstanceUri, selectedTemplate.id, isConnected])

  // Reset selection when changing canvas template
  const handleTemplateChange = (template: CanvasTemplate) => {
    setSelectedTemplate(template)
    setSelectedItem(null)
    setExpandedSection(null)
    setSelectedInstanceUri(null)
    setSelectedInstanceName(null)
    setShowPropertyPane(false)
    setFocusedSectionId(null)
  }

  // Toggle AI panel with keyboard shortcut
  const toggleAIPanel = useCallback(() => {
    setShowAIPanel((prev) => !prev)
  }, [])

  // Toggle Document panel with keyboard shortcut
  const toggleDocumentPanel = useCallback(() => {
    setShowDocumentPanel((prev) => !prev)
  }, [])

  // Keyboard shortcuts: Cmd/Ctrl+I (AI), Cmd/Ctrl+D (Documents)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault()
        toggleAIPanel()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault()
        toggleDocumentPanel()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleAIPanel, toggleDocumentPanel])

  // Handle section focus for AI context. The Canvas Coach panel does NOT
  // auto-open on focus — users open it explicitly via Cmd+I or by invoking
  // Skerpa. Auto-pop-up was disruptive and trained users to dismiss it.
  const handleSectionFocus = useCallback((sectionId: string) => {
    setFocusedSectionId(sectionId)
  }, [])

  const handleSelectInstance = (uri: string, name: string) => {
    setSelectedInstanceUri(uri)
    setSelectedInstanceName(name)
    setSelectedItem(null) // Clear item selection when switching instances
  }

  const handleInstanceCreated = (uri: string, name: string) => {
    setShowCreateInstanceDialog(false)
    setSelectedInstanceUri(uri)
    setSelectedInstanceName(name)
  }

  // Handle document analysis completion (entity-first pipeline via documents3)
  const handleDocumentAnalyzed = useCallback((result: Document2WithEntities) => {
    console.log('Document analyzed:', result.document.title)
    console.log(`Found ${result.entities.length} entities stored in graph`)
    // Note: Entities are stored in Fuseki graph via documents3
    // User can browse via Documents3 page or entity selection callback
  }, [])

  // Handle entity selection from the document panel
  const handleEntitySelected = useCallback((entity: Document2Entity, doc: Document2WithEntities) => {
    console.log('Entity selected:', entity.name, 'from document:', doc.document.title)
    // TODO: Could highlight the entity or navigate to detail view
  }, [])

  // Handle adding extracted entity to canvas section
  // Creates a proper ontology instance via backend and links it to the canvas section
  const handleAddEntityToCanvas = useCallback(async (entity: ExtractedEntity, sectionId: string) => {
    if (!selectedInstanceUri) {
      console.error('Cannot add entity: no canvas instance selected')
      return
    }

    if (!currentAccount?.id || !currentProject?.id) {
      console.error('Cannot add entity: no account or project context')
      return
    }

    try {
      // Step 1: Resolve entity type to ontology class
      const resolved = resolveEntityType(entity.type)
      if (!resolved.entityType) {
        console.warn(`Unknown entity type: ${entity.type}, falling back to notes`)
        // Fallback: add to notes if type is unknown
        const currentSection = filteredSectionData.find(s => s.sectionId === sectionId)
        const currentNotes = currentSection?.notes || ''
        const entityBullet = `• ${entity.label}${entity.description ? ` - ${entity.description}` : ''}`
        const newNotes = currentNotes ? `${currentNotes}\n${entityBullet}` : entityBullet
        await updateSectionNotes.mutateAsync({ canvasUri: selectedInstanceUri, sectionId, notes: newNotes })
        return
      }

      // Step 2: Get the ontology class URI for linking
      const typeConfig = ENTITY_TYPE_NAMESPACES[resolved.entityType]
      if (!typeConfig) {
        console.error(`No namespace config for entity type: ${resolved.entityType}`)
        return
      }
      const typeUri = `${typeConfig.namespace}${resolved.entityType}`

      console.log('Creating ontology instance via backend:', {
        canvasUri: selectedInstanceUri,
        sectionId,
        entityType: resolved.entityType,
        label: entity.label,
      })

      // Step 3: Create instance in knowledge graph via backend API
      // This ensures proper project graph isolation and namespace handling
      const { entityUri: instanceUri } = await sparqlApi.createEntity(
        currentAccount.id,
        currentProject.id,
        {
          entityType: resolved.entityType,
          label: entity.label,
          description: entity.description,
        }
      )

      console.log('Instance created via backend:', instanceUri)

      // Step 4: Link instance to canvas section
      await linkItemToSection.mutateAsync({
        canvasUri: selectedInstanceUri,
        sectionId,
        itemUri: instanceUri,
        itemLabel: entity.label,
        itemType: typeUri,
        note: entity.description,
      })

      // Invalidate queries to refresh tree view and canvas sections
      queryClient.invalidateQueries({ queryKey: ['instances'] })
      queryClient.invalidateQueries({ queryKey: ['instanceCount'] })
      queryClient.invalidateQueries({ queryKey: ['ontologyInstances'] })
      queryClient.invalidateQueries({ queryKey: ['entityCounts'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })

      console.log('Entity added to knowledge graph and linked to canvas:', instanceUri)
    } catch (err) {
      console.error('Failed to add entity to knowledge graph:', err)
    }
  }, [selectedInstanceUri, filteredSectionData, updateSectionNotes, linkItemToSection, currentAccount, currentProject, queryClient])

  // Handle entities change from Canvas Coach
  const handleEntitiesChange = useCallback((entities: ExtractedEntity[]) => {
    setPendingEntities(entities)
  }, [])

  // Handle controls ready from Canvas Coach
  const handleControlsReady = useCallback((controls: EntityControlFunctions) => {
    setEntityControls(controls)
  }, [])

  // Handle approving a pending entity from canvas section
  const handleApproveEntity = useCallback(async (entity: PendingEntity, sectionId: string) => {
    // Add to canvas via SPARQL
    await handleAddEntityToCanvas(entity as ExtractedEntity, sectionId)
    // Remove from pending entities via coach hook
    entityControls?.approveEntity(entity as ExtractedEntity)
  }, [handleAddEntityToCanvas, entityControls])

  // Handle dismissing a pending entity from canvas section
  const handleDismissEntity = useCallback((entity: PendingEntity) => {
    entityControls?.dismissEntity(entity as ExtractedEntity)
  }, [entityControls])

  // Unlink a linked item (URI-backed) from a canvas section
  const handleUnlinkItem = useCallback((sectionId: string, itemUri: string) => {
    if (!selectedInstanceUri) return
    unlinkItemFromSection.mutate({
      canvasUri: selectedInstanceUri,
      sectionId,
      itemUri,
    })
    // Clear selection if the removed item was open in the detail pane
    if (selectedItem?.uri === itemUri) {
      setSelectedItem(null)
      setShowPropertyPane(false)
    }
  }, [selectedInstanceUri, unlinkItemFromSection, selectedItem])

  // Remove a note-only item (text bullet without a graph URI) from a section
  const handleRemoveNoteItem = useCallback((sectionId: string, label: string) => {
    if (!selectedInstanceUri) return
    const section = filteredSectionData.find(s => s.sectionId === sectionId)
    const currentNotes = section?.notes || ''
    if (!currentNotes) return

    const cleaned = currentNotes
      .split('\n')
      .filter(line => {
        const stripped = line.trim().replace(/^[•\-\*]\s*/, '')
        return stripped !== label
      })
      .join('\n')

    updateSectionNotes.mutate({
      canvasUri: selectedInstanceUri,
      sectionId,
      notes: cleaned,
    })
  }, [selectedInstanceUri, filteredSectionData, updateSectionNotes])

  // Handle adding a suggestion from the AI suggestions popup/panel
  const handleAddSuggestion = useCallback(async (suggestion: { type: string; label: string; description?: string; sectionId: string; confidence: number }) => {
    const entity: ExtractedEntity = {
      type: suggestion.type,
      label: suggestion.label,
      description: suggestion.description,
      sectionId: suggestion.sectionId,
      confidence: suggestion.confidence,
    }
    await handleAddEntityToCanvas(entity, suggestion.sectionId)
  }, [handleAddEntityToCanvas])

  // Handle opening suggestions panel for a section
  const handleOpenSuggestions = useCallback((section: CanvasSection) => {
    setSuggestionsForSection(section)
    setShowSuggestionsPanel(true)
  }, [])

  // Handle closing suggestions panel
  const handleCloseSuggestions = useCallback(() => {
    setShowSuggestionsPanel(false)
    // Don't clear suggestionsForSection immediately - let animation complete
    setTimeout(() => setSuggestionsForSection(null), 200)
  }, [])


  if (!isConnected) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="max-w-[400px] text-center p-8">
          <AlertCircle className="w-12 h-12 text-warning mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Not Connected</h3>
          <p className="text-text-muted text-sm">
            Connect to Fuseki in Settings to view the canvas.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col gap-1.5">
      {!hideHeader && (
        <>
          {/* Template switcher row removed — templates are switched from the
              left rail / domain view. The scenario row below is the only
              chrome above the canvas grid. The configure-templates button
              moves inline into the scenario row's right edge in domain views. */}

          {/* Instance Navigator — the scenario tab strip. Always shown. */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <div className="flex-1 min-w-0">
              <CanvasInstanceNavigator
                templateId={selectedTemplate.id}
                selectedInstanceUri={selectedInstanceUri}
                onSelectInstance={handleSelectInstance}
                onCreateNew={() => setShowCreateInstanceDialog(true)}
                compact
              />
            </div>
            {isBMC && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: 'var(--color-bg-secondary, #f4f2ed)',
                borderRadius: 8,
                padding: 3,
                flexShrink: 0,
              }}>
                {(['canvas', 'outline'] as const).map(v => (
                  <button
                    key={v}
                    onClick={() => setViewMode(v)}
                    style={{
                      padding: '4px 12px',
                      border: 'none',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                      background: viewMode === v ? 'white' : 'transparent',
                      color: viewMode === v ? 'var(--color-fg, #1e3643)' : 'var(--color-fg-muted, #9a9490)',
                      boxShadow: viewMode === v ? '0 1px 3px rgba(29,26,21,0.08)' : 'none',
                      transition: 'all 0.15s',
                      fontFamily: 'inherit',
                      textTransform: 'capitalize',
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
            {domain && visibleTemplates.length > 1 && (
              <button
                onClick={() => setShowConfigModal(true)}
                className="p-1.5 mr-2 rounded-lg text-text-muted hover:text-text hover:bg-warm-100 transition-colors flex-shrink-0"
                title="Configure templates"
                aria-label="Configure templates"
              >
                <Settings2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </>
      )}

      {/* Canvas Content with Property Pane and AI Panel */}
      <div className="flex-1 flex min-h-0">
        {/* Canvas content — grid or outline based on viewMode */}
        <div
          className="flex-1 min-w-0 overflow-auto flex flex-col p-3.5"
          style={{ backgroundColor: 'var(--color-bg, #fcfcf9)' }}
        >
          {isBMC && viewMode === 'outline' ? (
            <CanvasBMCOutlineView
              canvasUri={selectedInstanceUri}
              sections={selectedTemplate.sections}
              sectionData={filteredSectionData}
              accountId={currentAccount?.id ?? ''}
              projectId={currentProject?.id ?? ''}
              onUnlinkItem={(sectionId, itemUri) => handleUnlinkItem(sectionId, itemUri)}
              onRequestCreate={(section) => setCreateForSection(section)}
              onSelectItem={(uri, sectionId) => {
                setSelectedItem({ uri, sectionId })
                setShowPropertyPane(true)
              }}
            />
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${selectedTemplate.gridColumns}, 1fr)`,
                  gridTemplateRows: `repeat(${selectedTemplate.gridRows}, 1fr)`,
                  gridTemplateAreas: selectedTemplate.gridAreas,
                  gap: '6px',
                  backgroundColor: 'var(--color-bg, #fcfcf9)',
                  border: '2px solid var(--color-bg, #fcfcf9)',
                  minHeight: '550px',
                  flex: '1 0 auto',
                }}
              >
                {selectedTemplate.sections.map((section) =>
                  section.sectionType === 'photo' ? (
                    <PhotoSectionCard
                      key={section.id}
                      section={section}
                      canvasInstanceUri={selectedInstanceUri}
                    />
                  ) : (
                    <CanvasSectionCard
                      key={section.id}
                      section={section}
                      isExpanded={expandedSection === section.id}
                      isFocused={focusedSectionId === section.id}
                      onToggleExpand={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                      onFocus={() => handleSectionFocus(section.id)}
                      onSelectItem={(uri) => {
                        setSelectedItem({ uri, sectionId: section.id })
                        setShowPropertyPane(true)
                      }}
                      selectedUri={selectedItem?.sectionId === section.id ? selectedItem.uri : null}
                      onRequestCreate={() => setCreateForSection(section)}
                      linkedItems={filteredSectionData.find(s => s.sectionId === section.id)?.linkedItems}
                      notes={filteredSectionData.find(s => s.sectionId === section.id)?.notes}
                      pendingEntities={pendingEntities.filter(e => e.sectionId === section.id)}
                      onApproveEntity={(entity) => handleApproveEntity(entity, section.id)}
                      onDismissEntity={handleDismissEntity}
                      canvasInstanceUri={selectedInstanceUri ?? undefined}
                      accountId={currentAccount?.id}
                      projectId={currentProject?.id}
                      apiKey={apiKey}
                      canvasContext={canvasContext}
                      onAddSuggestion={handleAddSuggestion}
                      onOpenSuggestionsPanel={handleOpenSuggestions}
                      onUnlinkItem={(itemUri) => handleUnlinkItem(section.id, itemUri)}
                      onRemoveNoteItem={(label) => handleRemoveNoteItem(section.id, label)}
                    />
                  )
                )}
              </div>
              {selectedTemplate.attribution && (
                <CanvasAttribution attribution={selectedTemplate.attribution} />
              )}
            </>
          )}
        </div>

        {/* Suggestions Panel - Shows AI suggestions for focused section */}
        {showSuggestionsPanel && suggestionsForSection && currentAccount && currentProject && apiKey && (
          <SectionSuggestionsPanel
            section={suggestionsForSection}
            canvasInstanceUri={selectedInstanceUri!}
            accountId={currentAccount.id}
            projectId={currentProject.id}
            apiKey={apiKey}
            canvasContext={canvasContext}
            onClose={handleCloseSuggestions}
            onAddSuggestion={handleAddSuggestion}
            className="w-[380px] border-l border-warm-200 flex-shrink-0"
          />
        )}

        {/* Property Pane - Shows instance or item details */}
        {showPropertyPane && (
          <div className="w-[380px] flex-shrink-0 bg-white border-l border-warm-200 overflow-hidden flex flex-col">
            {selectedItem ? (
              // Item Detail Panel
              <CanvasInstanceDetail
                instanceUri={selectedItem.uri}
                sectionId={selectedItem.sectionId}
                canvasInstanceUri={selectedInstanceUri}
                sections={selectedTemplate.sections}
                onClose={() => {
                  setSelectedItem(null)
                  setShowPropertyPane(false)
                }}
                onNavigate={(uri) => {
                  const section = selectedTemplate.sections.find((s) =>
                    s.classUris.some((classUri) => uri.includes(classUri.split('#')[0]))
                  )
                  if (section) {
                    setSelectedItem({ uri, sectionId: section.id })
                  } else {
                    setSelectedItem({ uri, sectionId: selectedItem.sectionId })
                  }
                }}
              />
            ) : instanceDetails ? (
              // Canvas Instance Property Pane
              <CanvasInstancePropertyPane
                instance={instanceDetails}
                onClose={() => setShowPropertyPane(false)}
              />
            ) : null}
          </div>
        )}

        {/* Canvas Coach Panel - Hidden when embedded in workspace */}
        {!hideAuxiliaryPanels && showAIPanel && currentAccount && currentProject && (
          <CanvasCoachPanel
            accountId={currentAccount.id}
            projectId={currentProject.id}
            instanceId={selectedInstanceUri ?? undefined}
            canvasType={(TEMPLATE_TO_CANVAS_TYPE[selectedTemplate.id] || 'bmc') as any}
            currentSection={focusedSectionId ?? undefined}
            canvasContent={canvasContentString}
            onSectionFocus={setFocusedSectionId}
            onAddToCanvas={handleAddEntityToCanvas}
            onEntitiesChange={handleEntitiesChange}
            onControlsReady={handleControlsReady}
            showEntitiesInPanel={false}
            className="w-[400px] border-l border-warm-200 flex-shrink-0"
          />
        )}

        {/* Document Processing Panel (Entity-First via documents3) - Hidden when embedded in workspace */}
        {!hideAuxiliaryPanels && showDocumentPanel && (
          <UnifiedDocumentPanel
            sourceContext="canvas"
            isExpanded={showDocumentPanel}
            onToggleExpand={toggleDocumentPanel}
            onDocumentAnalyzed={handleDocumentAnalyzed}
            onEntitySelected={handleEntitySelected}
            showKeyboardHint={true}
          />
        )}
      </div>

      {/* Create Ontology Instance Dialog */}
      {createForSection && (
        <CreateInstanceDialog
          classUri={createForSection.classUris[0]}
          classLabel={createForSection.title}
          color={createForSection.color}
          bgColor={createForSection.bgColor}
          onClose={() => setCreateForSection(null)}
          onCreated={(uri, label) => {
            // Link the new entity to this canvas section so it appears immediately
            if (selectedInstanceUri) {
              linkItemToSection.mutate({
                canvasUri: selectedInstanceUri,
                sectionId: createForSection.id,
                itemUri: uri,
                itemLabel: label,
                itemType: createForSection.classUris[0],
              })
            }
          }}
        />
      )}

      {/* Create Canvas Instance Dialog */}
      {showCreateInstanceDialog && (
        <CreateCanvasInstanceDialog
          templateId={selectedTemplate.id}
          onClose={() => setShowCreateInstanceDialog(false)}
          onCreated={handleInstanceCreated}
        />
      )}
      {showConfigModal && (
        <CanvasDomainConfigModal
          config={domainConfig}
          onSave={handleSaveConfig}
          onClose={() => setShowConfigModal(false)}
        />
      )}
    </div>
  )
}

// ============================================================================
// CANVAS INSTANCE PROPERTY PANE
// ============================================================================

interface CanvasInstancePropertyPaneProps {
  instance: {
    uri: string
    name: string
    description?: string
    templateId: string
    status: string
    createdAt: string
    updatedAt: string
  }
  onClose: () => void
}

function CanvasInstancePropertyPane({ instance, onClose }: CanvasInstancePropertyPaneProps) {
  const updateInstance = useUpdateCanvasInstance()
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editName, setEditName] = useState(instance.name)
  const [editDescription, setEditDescription] = useState(instance.description || '')

  const handleSaveName = async () => {
    if (editName.trim() && editName !== instance.name) {
      await updateInstance.mutateAsync({
        uri: instance.uri,
        updates: { name: editName.trim() },
      })
    }
    setIsEditingName(false)
  }

  const handleSaveDescription = async () => {
    if (editDescription !== instance.description) {
      await updateInstance.mutateAsync({
        uri: instance.uri,
        updates: { description: editDescription },
      })
    }
    setIsEditingDescription(false)
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-warm-200 flex items-center justify-between">
        <h3 className="font-semibold text-text">Canvas Properties</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-warm-100 rounded transition-colors"
        >
          <X className="w-4 h-4 text-text-muted" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 block">
            Name
          </label>
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName()
                  if (e.key === 'Escape') setIsEditingName(false)
                }}
                onBlur={handleSaveName}
                autoFocus
                className="flex-1 px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          ) : (
            <div
              onClick={() => {
                setEditName(instance.name)
                setIsEditingName(true)
              }}
              className="px-3 py-2 bg-warm-50 rounded-lg text-sm cursor-pointer hover:bg-warm-100 transition-colors flex items-center justify-between group"
            >
              <span className="font-medium">{instance.name}</span>
              <Edit3 className="w-3.5 h-3.5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 block">
            Description
          </label>
          {isEditingDescription ? (
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setIsEditingDescription(false)
              }}
              onBlur={handleSaveDescription}
              autoFocus
              rows={3}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
              placeholder="Add a description..."
            />
          ) : (
            <div
              onClick={() => {
                setEditDescription(instance.description || '')
                setIsEditingDescription(true)
              }}
              className="px-3 py-2 bg-warm-50 rounded-lg text-sm cursor-pointer hover:bg-warm-100 transition-colors min-h-[60px] group"
            >
              {instance.description ? (
                <span className="text-text">{instance.description}</span>
              ) : (
                <span className="text-text-muted italic">Click to add description...</span>
              )}
              <Edit3 className="w-3.5 h-3.5 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity float-right" />
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2 block">
            Status
          </label>
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-text-muted" />
            <Badge
              variant="outline"
              className={cn(
                instance.status === 'active' && 'bg-success-bg text-success-fg border-success/20',
                instance.status === 'draft' && 'bg-warning-bg text-warning-fg border-warning/20',
                instance.status === 'archived' && 'bg-info-bg text-info-fg border-info/20'
              )}
            >
              {instance.status}
            </Badge>
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-3 pt-4 border-t border-warm-200">
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Calendar className="w-4 h-4" />
            <span>Created: {formatDate(instance.createdAt)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Calendar className="w-4 h-4" />
            <span>Updated: {formatDate(instance.updatedAt)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// CANVAS SECTION CARD
// ============================================================================

interface PendingEntity {
  type: string
  label: string
  description?: string
  sectionId?: string
  confidence: number
}

/** Item to display in a canvas section */
interface CanvasSectionItem {
  uri?: string // Optional - text items don't have URIs
  label: string
  isFromNotes?: boolean // True if parsed from notes text
}

interface CanvasSectionCardProps {
  section: CanvasSection
  isExpanded: boolean
  isFocused?: boolean
  onToggleExpand: () => void
  onFocus?: () => void
  onSelectItem: (uri: string) => void
  selectedUri: string | null
  onRequestCreate: () => void
  /** Linked items from section data */
  linkedItems?: Array<{ instanceUri: string; label: string; note?: string }>
  /** Notes text from section data */
  notes?: string
  pendingEntities?: PendingEntity[]
  onApproveEntity?: (entity: PendingEntity) => void
  onDismissEntity?: (entity: PendingEntity) => void
  /** AI Suggestions props (legacy popup support) */
  canvasInstanceUri?: string
  accountId?: string
  projectId?: string
  apiKey?: string
  canvasContext?: Record<string, string[]>
  onAddSuggestion?: (suggestion: { type: string; label: string; description?: string; sectionId: string; confidence: number }) => void
  /** Open suggestions panel (new) */
  onOpenSuggestionsPanel?: (section: CanvasSection) => void
  /** Remove a linked item (URI-backed) from this section */
  onUnlinkItem?: (itemUri: string) => void
  /** Remove a note-only item (no URI) from this section */
  onRemoveNoteItem?: (label: string) => void
}

const MARKET_PERSONA_URI = 'http://www.intellispot.com/ontology/market#Persona'

const CHIP_OVERFLOW_THRESHOLD = 12
const CHIP_OVERFLOW_VISIBLE = 11

interface CanvasItemChipProps {
  label: string
  domainColor: string
  isSelected: boolean
  isFromNotes: boolean
  onClick: () => void
  canRemove: boolean
  onRemove: () => void
}

function CanvasItemChip({ label, domainColor, isSelected, isFromNotes, onClick, canRemove, onRemove }: CanvasItemChipProps) {
  const [hovered, setHovered] = useState(false)
  const showRemove = canRemove && hovered

  return (
    <span
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        height: 26,
        paddingLeft: 9,
        paddingRight: showRemove ? 26 : 11,
        background: isSelected
          ? alpha(domainColor, 0.14)
          : hovered
            ? alpha(domainColor, 0.07)
            : 'var(--color-bg-card, #ffffff)',
        border: `1px ${isFromNotes ? 'dashed' : 'solid'} ${
          isSelected
            ? alpha(domainColor, 0.56)
            : hovered
              ? alpha(domainColor, 0.38)
              : 'var(--color-border-strong, #d6d4cb)'
        }`,
        borderRadius: 999,
        fontSize: 12.5,
        lineHeight: 1.2,
        color: 'var(--color-primary-800, #111e25)',
        cursor: 'pointer',
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        opacity: isFromNotes ? 0.92 : 1,
        transition: 'border-color 0.12s, background-color 0.12s, padding-right 0.12s',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: 999,
          background: domainColor,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 200,
        }}
      >
        {label}
      </span>
      {canRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          style={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 18,
            height: 18,
            border: 0,
            background: 'transparent',
            color: 'var(--color-fg-muted, #767670)',
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: showRemove ? 1 : 0,
            transition: 'opacity 0.12s, color 0.12s, background-color 0.12s',
            fontSize: 12,
            lineHeight: 1,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--color-text)'
            e.currentTarget.style.background = 'var(--color-warm-200, #ebe9e0)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--color-fg-muted, #767670)'
            e.currentTarget.style.background = 'transparent'
          }}
          title={`Remove ${label}`}
          aria-label={`Remove ${label}`}
          tabIndex={showRemove ? 0 : -1}
        >
          ×
        </button>
      )}
    </span>
  )
}

function CanvasSectionCard({ section, isExpanded, isFocused, onToggleExpand, onFocus, onSelectItem, selectedUri, onRequestCreate, linkedItems = [], notes = '', pendingEntities = [], onApproveEntity, onDismissEntity, canvasInstanceUri, accountId, projectId, apiKey, canvasContext, onAddSuggestion, onOpenSuggestionsPanel, onUnlinkItem, onRemoveNoteItem }: CanvasSectionCardProps) {
  const Icon = section.icon
  const themeStyles = getCanvasSectionThemeStyles(section.theme)
  const [showSuggestionsPopup, setShowSuggestionsPopup] = useState(false)
  const [showAllItems, setShowAllItems] = useState(false)
  const sparklesButtonRef = useRef<HTMLButtonElement>(null)

  // Persona chips: shown in sections whose classUris include market:Persona
  const isPersonaSection = section.classUris.includes(MARKET_PERSONA_URI)
  const { personas } = usePersonasForCanvas()
  const personaSessionMap = usePersonaWorkshopSessionsMap()
  const linkedUris = useMemo(() => new Set(linkedItems.map(i => i.instanceUri)), [linkedItems])
  const unlinkedPersonas = useMemo(
    () => (isPersonaSection ? personas.filter(p => !linkedUris.has(p.uri)) : []),
    [isPersonaSection, personas, linkedUris]
  )

  // Derive items from linkedItems + parsed notes
  const items = useMemo(() => {
    const result: CanvasSectionItem[] = []

    // Add linked items (these have URIs and can be clicked)
    for (const item of linkedItems) {
      result.push({
        uri: item.instanceUri,
        label: item.label || item.note || 'Unnamed item',
        isFromNotes: false,
      })
    }

    // Parse notes into bullet points (these are text-only)
    if (notes.trim()) {
      const lines = notes.split('\n')
      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed) {
          // Remove bullet point prefixes (•, -, *)
          const label = trimmed.replace(/^[•\-\*]\s*/, '')
          if (label) {
            result.push({
              label,
              isFromNotes: true,
            })
          }
        }
      }
    }

    return result
  }, [linkedItems, notes])

  // Short per-section subtitles for BMC (design-specified). Other canvases fall back
  // to the section description truncated to its first clause.
  const BMC_SUBTITLE: Record<string, string> = {
    'key-partners': 'Who you work with',
    'key-activities': 'What you do',
    'key-resources': 'What you need',
    'value-propositions': 'Why they buy',
    'customer-relationships': 'How you engage',
    'channels': 'How you reach them',
    'customer-segments': 'Who has the problem',
    'cost-structure': 'Where money goes',
    'revenue-streams': 'Where money comes from',
  }

  // Skip photo sections (custom treatment handled elsewhere)
  const applyRedesign = section.sectionType !== 'photo'

  // Derive domain from the first class URI's ontology namespace
  const firstClassUri = section.classUris[0] ?? ''
  const sectionDomain: 'market' | 'business' | 'solution' | null =
    firstClassUri.includes('/market#') ? 'market'
    : firstClassUri.includes('/business#') ? 'business'
    : firstClassUri.includes('/solution#') ? 'solution'
    : null

  const domainLabel = sectionDomain
    ? sectionDomain === 'market' ? 'Market' : sectionDomain === 'business' ? 'Business' : 'Solution'
    : null

  const compactDescription = (() => {
    const raw = section.description ?? ''
    const firstClause = raw.split(/[—.?!]/)[0] ?? raw
    const trimmed = firstClause.trim()
    return trimmed.length > 48 ? trimmed.slice(0, 46).trim() + '…' : trimmed
  })()

  const subtitleText = BMC_SUBTITLE[section.id] ?? compactDescription

  const itemCount = (linkedItems?.length ?? 0) + (notes.trim() ? notes.split('\n').filter(l => l.trim()).length : 0)
  const sectionConfidence = (() => {
    if (itemCount === 0) return 0
    if (itemCount === 1) return 28
    if (itemCount === 2) return 42
    if (itemCount === 3) return 58
    if (itemCount === 4) return 68
    if (itemCount === 5) return 74
    return Math.min(90, 74 + (itemCount - 5) * 3)
  })()
  const sectionConfidenceLabel = sectionConfidence === 0
    ? 'Empty'
    : sectionConfidence >= 75 ? 'Validated'
    : sectionConfidence >= 55 ? 'In review'
    : sectionConfidence >= 35 ? 'Draft'
    : 'Low'
  const aiSignalText = applyRedesign && itemCount < 3
    ? (itemCount === 0
        ? `no entries yet — start with one ${section.title.toLowerCase()}`
        : `only ${itemCount} item${itemCount === 1 ? '' : 's'} — add more evidence to strengthen`)
    : null

  return (
    <div
      onClick={() => onFocus?.()}
      style={{
        gridArea: section.gridArea,
        // Match the Segments module cards, which use the Tailwind white surface.
        // The grid container paints the 2px divider.
        backgroundColor: section.bgColor || 'white',
        boxShadow: isFocused ? `inset 0 0 0 2px ${alpha(section.color, 0.28)}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        transition: 'background-color 0.18s, box-shadow 0.18s',
        cursor: 'pointer',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 8px 8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
        }}
      >
        <div
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: 'pointer', minWidth: 0 }}
        >
          {applyRedesign ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: 5,
                background: themeStyles.tintColor,
                color: section.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon style={{ width: 12, height: 12 }} strokeWidth={2} />
            </div>
          ) : (
            <Icon style={{ width: '16px', height: '16px', color: section.color, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                title={
                  applyRedesign && (domainLabel || subtitleText)
                    ? domainLabel
                      ? `${section.title} — ${subtitleText} · ${domainLabel}`
                      : `${section.title} — ${subtitleText}`
                    : section.title
                }
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  color: applyRedesign ? 'var(--color-text)' : section.color,
                  letterSpacing: '-0.005em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {section.title}
              </span>
              {applyRedesign ? (
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10.5,
                    color: 'var(--color-text-muted)',
                    fontWeight: 500,
                    background: 'var(--color-warm-100)',
                    padding: '1px 6px',
                    borderRadius: 999,
                    flexShrink: 0,
                  }}
                >
                  {items.length}{pendingEntities.length > 0 && <span style={{ opacity: 0.7 }}> +{pendingEntities.length}</span>}
                </span>
              ) : (
                <Badge variant={themeStyles.badgeVariant} style={{ fontSize: '10px' }}>
                  {items.length}{pendingEntities.length > 0 && <span style={{ opacity: 0.7 }}> +{pendingEntities.length}</span>}
                </Badge>
              )}
              {applyRedesign && (
                <span
                  title={`Confidence ${sectionConfidence}% · ${sectionConfidenceLabel} · ${itemCount === 0 ? 'no evidence yet' : `${itemCount} ${itemCount === 1 ? 'entry' : 'entries'}`}`}
                  aria-label={`Confidence ${sectionConfidence} percent, ${sectionConfidenceLabel}`}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background:
                      sectionConfidence === 0 ? 'var(--color-warm-300)'
                      : sectionConfidence >= 75 ? 'var(--color-success, #1F9D55)'
                      : sectionConfidence >= 35 ? 'var(--color-warning, #E07B1A)'
                      : 'var(--color-danger, #E24B4B)',
                    flexShrink: 0,
                    cursor: 'help',
                  }}
                />
              )}
            </div>
          </div>
        </div>
        {/* Info toggle — reveals the section's editorial description and ontology classes */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand()
          }}
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            border: 0,
            background: isExpanded ? 'var(--color-warm-150, #f1efe8)' : 'transparent',
            color: isExpanded ? 'var(--color-fg-secondary, #4b5e69)' : 'var(--color-fg-faint, #a3a39d)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background-color 0.12s, color 0.12s',
          }}
          onMouseEnter={(e) => {
            if (!isExpanded) {
              e.currentTarget.style.background = 'var(--color-warm-150, #f1efe8)'
              e.currentTarget.style.color = 'var(--color-fg-secondary, #4b5e69)'
            }
          }}
          onMouseLeave={(e) => {
            if (!isExpanded) {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--color-fg-faint, #a3a39d)'
            }
          }}
          title={isExpanded ? 'Hide description' : 'What goes here?'}
          aria-label={isExpanded ? 'Hide description' : 'Show description'}
          aria-expanded={isExpanded}
        >
          <Info style={{ width: 14, height: 14 }} strokeWidth={1.7} />
        </button>
        {/* Sparkles button for AI suggestions - opens panel if available, else popup */}
        {canvasInstanceUri && accountId && projectId && apiKey && (onAddSuggestion || onOpenSuggestionsPanel) && (
          <div style={{ position: 'relative' }}>
            <button
              ref={sparklesButtonRef}
              onClick={(e) => {
                e.stopPropagation()
                if (onOpenSuggestionsPanel) {
                  onOpenSuggestionsPanel(section)
                } else {
                  setShowSuggestionsPopup(!showSuggestionsPopup)
                }
              }}
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                border: 0,
                background: showSuggestionsPopup ? alpha(section.color, 0.12) : 'transparent',
                color: showSuggestionsPopup ? section.color : 'var(--color-accent, #e84300)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: showSuggestionsPopup ? 1 : 0.85,
                flexShrink: 0,
                transition: 'background-color 0.12s, opacity 0.12s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1'
                if (!showSuggestionsPopup) e.currentTarget.style.background = 'var(--color-warm-150, #f1efe8)'
              }}
              onMouseLeave={(e) => {
                if (!showSuggestionsPopup) {
                  e.currentTarget.style.opacity = '0.85'
                  e.currentTarget.style.background = 'transparent'
                }
              }}
              title="Suggest with Skerpa"
              aria-label="Suggest with Skerpa"
            >
              <Sparkles style={{ width: 14, height: 14 }} strokeWidth={1.7} />
            </button>
            {/* Legacy popup - only shown if panel is not available */}
            {showSuggestionsPopup && !onOpenSuggestionsPanel && onAddSuggestion && (
              <SectionSuggestionsPopup
                section={section}
                canvasInstanceUri={canvasInstanceUri}
                accountId={accountId}
                projectId={projectId}
                apiKey={apiKey}
                canvasContext={canvasContext}
                isOpen={showSuggestionsPopup}
                onClose={() => setShowSuggestionsPopup(false)}
                onAddSuggestion={onAddSuggestion}
                anchorRef={sparklesButtonRef}
              />
            )}
          </div>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRequestCreate()
          }}
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            border: 0,
            background: 'transparent',
            color: 'var(--color-fg-muted, #767670)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background-color 0.12s, color 0.12s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--color-warm-150, #f1efe8)'
            e.currentTarget.style.color = 'var(--color-text)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = 'var(--color-fg-muted, #767670)'
          }}
          title={`Add ${section.title}`}
          aria-label={`Add ${section.title}`}
        >
          <Plus style={{ width: 14, height: 14 }} strokeWidth={1.7} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 12px 12px' }}>
        {items.length === 0 && pendingEntities.length === 0 && unlinkedPersonas.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '8px 0' }}>
            <p style={{ fontSize: '12px', color: 'var(--color-fg-faint, #a3a39d)', fontStyle: 'italic' }}>
              No entries yet
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {/* Pending entities from AI coach — dashed leading edge to read as proposals */}
            {pendingEntities.map((entity, index) => (
              <div
                key={`pending-${entity.label}-${index}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  height: 26,
                  padding: '0 4px 0 9px',
                  background: 'var(--color-bg-card, #ffffff)',
                  border: `1px dashed ${alpha(section.color, 0.45)}`,
                  borderRadius: 999,
                  fontSize: 12.5,
                  color: 'var(--color-text)',
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                }}
                title={`Pending suggestion · ${entity.label}`}
              >
                <span style={{ width: 5, height: 5, borderRadius: 999, background: section.color, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                  {entity.label}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onApproveEntity?.(entity)
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    border: 0,
                    background: 'var(--color-success-bg, #ecf5ee)',
                    color: 'var(--color-success-fg, #306d3f)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                    transition: 'background 0.15s',
                  }}
                  title="Add to canvas"
                  aria-label={`Approve ${entity.label}`}
                >
                  ✓
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDismissEntity?.(entity)
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    border: 0,
                    background: 'transparent',
                    color: 'var(--color-fg-muted, #767670)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 12,
                    flexShrink: 0,
                    transition: 'color 0.15s, background 0.15s',
                  }}
                  title="Dismiss"
                  aria-label={`Dismiss ${entity.label}`}
                >
                  ×
                </button>
              </div>
            ))}
            {/* Section items — quiet hairline chips with hover-revealed × remove */}
            {(() => {
              const overflow = items.length > CHIP_OVERFLOW_THRESHOLD
              const visibleCount = !overflow || showAllItems ? items.length : CHIP_OVERFLOW_VISIBLE
              const visibleItems = items.slice(0, visibleCount)
              const hiddenCount = items.length - visibleItems.length
              return (
                <>
                  {visibleItems.map((item, index) => {
                    const canRemove = item.uri ? !!onUnlinkItem : !!onRemoveNoteItem
                    const isSelected = !!item.uri && selectedUri === item.uri
                    return (
                      <CanvasItemChip
                        key={item.uri || `note-${index}`}
                        label={item.label}
                        domainColor={section.color}
                        isSelected={isSelected}
                        isFromNotes={!!item.isFromNotes}
                        onClick={() => item.uri && onSelectItem(item.uri)}
                        canRemove={canRemove}
                        onRemove={() => {
                          if (item.uri) onUnlinkItem?.(item.uri)
                          else onRemoveNoteItem?.(item.label)
                        }}
                      />
                    )
                  })}
                  {hiddenCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowAllItems(true)
                      }}
                      style={{
                        height: 26,
                        padding: '0 10px',
                        background: 'transparent',
                        border: 0,
                        fontFamily: 'var(--font-mono, "Geist Mono")',
                        fontSize: 11,
                        color: 'var(--color-fg-muted, #767670)',
                        cursor: 'pointer',
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-text)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fg-muted, #767670)')}
                      title={`Show ${hiddenCount} more`}
                    >
                      +{hiddenCount} more…
                    </button>
                  )}
                  {overflow && showAllItems && items.length > CHIP_OVERFLOW_VISIBLE && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowAllItems(false)
                      }}
                      style={{
                        height: 26,
                        padding: '0 10px',
                        background: 'transparent',
                        border: 0,
                        fontFamily: 'var(--font-mono, "Geist Mono")',
                        fontSize: 11,
                        color: 'var(--color-fg-muted, #767670)',
                        cursor: 'pointer',
                      }}
                      title="Show less"
                    >
                      Show less
                    </button>
                  )}
                </>
              )
            })()}
            {/* Persona suggestion chips — unlinked graph personas */}
            <PersonaChipList
              personas={unlinkedPersonas}
              sessionMap={personaSessionMap}
              sectionTheme={section.theme}
              sectionColor={section.color}
            />
          </div>
        )}
      </div>

      {/* Expanded Description — editorial info panel */}
      {isExpanded && (
        <div
          style={{
            margin: '0 12px 12px',
            padding: '10px 0 10px 14px',
            borderLeft: `2px solid ${alpha(section.color, 0.45)}`,
            background: 'transparent',
          }}
        >
          <p
            style={{
              fontSize: 12.5,
              color: 'var(--color-fg-secondary, #4b5e69)',
              lineHeight: 1.55,
              maxWidth: '60ch',
              margin: 0,
            }}
          >
            <strong style={{ color: 'var(--color-text)', fontWeight: 600 }}>{section.title}.</strong>{' '}
            {section.description}
          </p>
          {section.classUris.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {section.classUris.map((uri) => {
                const cls = ALL_CLASSES.find((c) => c.uri === uri)
                const localName = cls ? cls.localName : getLocalName(uri)
                return (
                  <span
                    key={uri}
                    style={{
                      fontFamily: 'var(--font-mono, "Geist Mono")',
                      fontSize: 10.5,
                      color: 'var(--color-fg-muted, #767670)',
                      background: 'var(--color-warm-100, #f8f6f0)',
                      border: '1px solid var(--color-border-subtle, #efedea)',
                      padding: '1px 6px',
                      borderRadius: 4,
                    }}
                    title={uri}
                  >
                    {localName}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      )}

      {applyRedesign && aiSignalText && (
        <span
          aria-label="Skerpa has a suggestion for this section"
          title={`Skerpa: ${aiSignalText}`}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: section.color,
            boxShadow: `0 0 0 3px ${alpha(section.color, 0.18)}`,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
// CANVAS INSTANCE DETAIL
// ============================================================================

interface CanvasInstanceDetailProps {
  instanceUri: string
  sectionId: string
  canvasInstanceUri: string | null
  sections: CanvasSection[]
  onClose: () => void
  onNavigate: (uri: string) => void
}

function CanvasInstanceDetail({ instanceUri, sectionId, canvasInstanceUri, sections, onClose, onNavigate }: CanvasInstanceDetailProps) {
  const queryClient = useQueryClient()
  const unlinkItemFromSection = useUnlinkItemFromSection()

  // Find section by ID to get colors
  const section = sections.find((s) => s.id === sectionId)
  const color = section?.color || '#8a847a'
  const bgColor = section?.bgColor

  const handleDeleted = async () => {
    if (canvasInstanceUri) {
      await unlinkItemFromSection.mutateAsync({
        canvasUri: canvasInstanceUri,
        sectionId,
        itemUri: instanceUri,
      })
    }

    queryClient.invalidateQueries({ queryKey: ['canvasInstances'] })
    queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
  }

  return (
    <InstanceDetailPanel
      instanceUri={instanceUri}
      color={color}
      bgColor={bgColor}
      onClose={onClose}
      onNavigate={onNavigate}
      onDeleted={handleDeleted}
    />
  )
}
